import React, { memo, useMemo } from 'react';
import { Row } from 'antd/es/grid';
import { Typography } from 'antd';
import { CountSelector } from 'common/components/CountSelector';
import { useAppSelector } from 'app/store/store.hooks';
import { ECategoryType, EProductType, IMenuItem } from 'entities/Menu/Menu.models';
import 'app/assets/sass/components/item-card.scss';
import { EStoreState, IStoreModel } from 'entities/Store/Store.models';
import { MenuItemImage } from 'entities/Menu/components/MenuItemImage';
import { MealBuilder } from 'entities/Menu/components/MealBuilder';
import { AddonsCategoriesList } from 'entities/AddOns/components/AddonsCategoriesList';
import { ICartAddItemAddOnParams, ICartAddItemMealBuilderPickParams } from 'entities/Cart/Cart.models';

interface IComponentProps {
  item: IMenuItem;
  quantity: number;
  selectedStore?: IStoreModel;
  onQuantityChange: (quantity: number) => void;
  mealBuilderPicks: ICartAddItemMealBuilderPickParams[];
  onMealBuilderPicksChange: (picks: ICartAddItemMealBuilderPickParams[]) => void;
  onMealBuilderValidityChange?: (isValid: boolean) => void;
  addOns: ICartAddItemAddOnParams[];
  onAddOnsChange: (addOns: ICartAddItemAddOnParams[]) => void;
}

const ScannedItemCardComponent: React.FC<IComponentProps> = ({
  item,
  quantity,
  selectedStore,
  onQuantityChange,
  mealBuilderPicks,
  onMealBuilderPicksChange,
  onMealBuilderValidityChange,
  addOns,
  onAddOnsChange,
}) => {
  const { menuCollection, menuAddOns } = useAppSelector((state) => state.menu);
  const { price, name, description, image, productType } = item || {};
  const isStoreOffline = selectedStore && selectedStore.state !== EStoreState.Online;

  const { categoriesToAsset: addOnsCategories } = menuAddOns || {};
  const hasBEAddOns = !!addOnsCategories?.length;

  const category = useMemo(
    () =>
      menuCollection?.categoriesToAsset?.find((cat) =>
        cat.menuItemsToAsset?.some((menuItem) => menuItem.menuItemId === item?.menuItemId),
      ),
    [menuCollection, item?.menuItemId],
  );

  const isVisibleAddons = (category?.type as ECategoryType) === ECategoryType.IsAddOnsEnable && hasBEAddOns && !isStoreOffline;
  const isVisibleMealBuilder = productType === EProductType.SandwichOnly || productType === EProductType.Combo;

  return (
    <div className="menu-details fs-xs menu-card mt-8">
      <div className="menu-details__content">
        <Row justify="space-between" className="d-flex_no-wrap">
          <div>
            {name && <div className="menu-details__header">{name}</div>}
            {description && (
              <Typography.Paragraph className="menu-details__description word-break">{description}</Typography.Paragraph>
            )}

            {selectedStore && (
              <CountSelector
                label={
                  <Row className="fs" align="middle">
                    P&nbsp;<span className="fs-xs font-weight-7">{price}</span>
                  </Row>
                }
                value={quantity}
                onChange={onQuantityChange}
                min={1}
                className={isStoreOffline ? 'offline' : ''}
              />
            )}
          </div>

          {image && (
            <div className="menu-details__image-wrapper">
              <MenuItemImage src={image} className="menu-details__image" alt={name} />
            </div>
          )}
        </Row>

        {isVisibleMealBuilder && (
          <MealBuilder
            menuItem={item}
            mealBuilderPicks={mealBuilderPicks}
            onUpdate={onMealBuilderPicksChange}
            onValidityChange={onMealBuilderValidityChange}
          />
        )}

        {isVisibleAddons && (
          <AddonsCategoriesList allAddOnsCategories={addOnsCategories} itemAddOns={addOns} update={onAddOnsChange} />
        )}
      </div>
    </div>
  );
};

export const ScannedItemCard = memo(ScannedItemCardComponent);
